import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '../../../graphql/client';
import {
  M_DELETE_VIEW_HIERARCHY,
  M_DUPLICATE_VIEW_HIERARCHY,
  M_SWAP_CHILD_SORTS,
} from '../../../graphql/operations';
import type { ChildViewEdge, View } from '../../../types/forge';
import { useEditorStore } from '../store/editorStore';

interface AppData {
  FrontendApp_by_pk: { id: string; name: string; main_view_id: string | null };
  View: View[];
}

interface ParentInfo {
  parentId: string;
  edgeId: string;
  sort: number;
}

/**
 * View tree with inline move-up / move-down / delete affordances.
 *
 * Pre-computes a parent-lookup map so each row knows:
 *   - which View_View edge connects it to its parent (for sort swaps)
 *   - its previous + next siblings (so we can disable the move buttons
 *     when the row is at the top/bottom of its parent's children)
 *
 * Cross-surface selection sync:
 *   - Canvas click → editorStore.selectView() → this tree highlights
 *     the matching row, auto-expands every ancestor that was collapsed
 *     (so the row is actually visible), and scrolls the row into view.
 *   - Tree click  → editorStore.selectView() → Canvas's preview
 *     iframe re-renders the screen containing the new selection and
 *     draws the selection outline (handled engine-side in ForgeView).
 *
 * Collapse / expand:
 *   - Each row with children gets a chevron toggle. Collapsed state
 *     persists in localStorage, keyed per app, so re-opening the
 *     builder restores the author's preferred view.
 *   - Ancestors of the current selection are auto-expanded — selection
 *     should never hide behind a collapsed row.
 *
 * Mutations are immediate — no buffering, no confirmation dialog (yet).
 */
export function ViewTree({ appData }: { appData: AppData }) {
  const selected = useEditorStore((s) => s.selectedViewId);
  const select = useEditorStore((s) => s.selectView);
  const appId = appData.FrontendApp_by_pk?.id;
  const queryClient = useQueryClient();
  const [hoverId, setHoverId] = useState<string | null>(null);

  // (childViewId → ParentInfo). Memo keeps the map stable across
  // selection changes so the auto-expand effect isn't fighting churn.
  const parentByChild = useMemo(() => {
    const map = new Map<string, ParentInfo>();
    for (const v of appData.View) {
      for (const edge of v.child_views ?? []) {
        map.set(edge.related_view_id, {
          parentId: v.id,
          edgeId: edge.id,
          sort: edge.sort,
        });
      }
    }
    return map;
  }, [appData.View]);

  /* --------------------------- Collapse state -------------------------- */

  const storageKey = appId ? `forge.builder.viewTree.collapsed.${appId}` : null;
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    loadCollapsed(storageKey),
  );

  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...collapsed]));
    } catch {
      // localStorage write failures are non-fatal
    }
  }, [storageKey, collapsed]);

  const toggleCollapsed = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsed(new Set()), []);

  const collapseAll = useCallback(() => {
    // Collapse every node that has children. Root is also collapsible —
    // collapsing it gives a one-row tree with just the root visible.
    const ids = new Set<string>();
    for (const v of appData.View) {
      if ((v.child_views?.length ?? 0) > 0) ids.add(v.id);
    }
    setCollapsed(ids);
  }, [appData.View]);

  /* ---------------------- Auto-expand ancestors ----------------------- */
  // When the selection changes (typically because the user clicked in
  // the canvas iframe), walk up to the root and remove every ancestor
  // from `collapsed`. This guarantees the selected row is visible —
  // otherwise canvas-driven selection would silently hide inside a
  // collapsed subtree and the highlight would never appear.
  useEffect(() => {
    if (!selected) return;
    setCollapsed((prev) => {
      let mutated: Set<string> | null = null;
      let cursor = parentByChild.get(selected);
      while (cursor) {
        if (prev.has(cursor.parentId)) {
          if (!mutated) mutated = new Set(prev);
          mutated.delete(cursor.parentId);
        }
        cursor = parentByChild.get(cursor.parentId);
      }
      return mutated ?? prev;
    });
  }, [selected, parentByChild]);

  /* ----------------------- Scroll selection into view ------------------ */
  const selectedRowRef = useRef<HTMLDivElement | null>(null);
  // Re-run when collapsed changes too, so the scroll happens AFTER
  // the auto-expand effect has revealed the row.
  useEffect(() => {
    if (!selected) return;
    selectedRowRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selected, collapsed]);

  /* ------------------------------ Mutations ---------------------------- */

  const swap = useMutation({
    mutationFn: (vars: { aId: string; aSort: number; bId: string; bSort: number }) =>
      gql(M_SWAP_CHILD_SORTS, vars),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['app-data', appId] }),
  });

  const del = useMutation({
    mutationFn: (viewId: string) => gql(M_DELETE_VIEW_HIERARCHY, { viewId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
      select(null);
    },
  });

  const duplicate = useMutation({
    mutationFn: (viewId: string) =>
      gql<{ duplicate_view_hierarchy: { id: string }[] }>(M_DUPLICATE_VIEW_HIERARCHY, { viewId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
      const newId = data.duplicate_view_hierarchy[0]?.id;
      if (newId) select(newId);
    },
  });

  const viewsById = useMemo(
    () => new Map<string, View>(appData.View.map((v) => [v.id, v])),
    [appData.View],
  );
  const rootId = appData.FrontendApp_by_pk?.main_view_id;

  if (!rootId) {
    return (
      <section style={styles.section}>
        <Header onExpandAll={expandAll} onCollapseAll={collapseAll} />
        <p style={styles.empty}>
          No root view set. Drop a component on the canvas to start.
        </p>
      </section>
    );
  }

  const handleMove = (viewId: string, direction: 'up' | 'down') => {
    const parent = parentByChild.get(viewId);
    if (!parent) return; // root — no parent to reorder under
    const parentView = viewsById.get(parent.parentId);
    if (!parentView) return;
    const sortedSiblings = [...(parentView.child_views ?? [])].sort(
      (a, b) => a.sort - b.sort,
    );
    const idx = sortedSiblings.findIndex((e) => e.related_view_id === viewId);
    const swapWithIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWithIdx < 0 || swapWithIdx >= sortedSiblings.length) return;
    const a = sortedSiblings[idx]!;
    const b = sortedSiblings[swapWithIdx]!;
    swap.mutate({ aId: a.id, aSort: b.sort, bId: b.id, bSort: a.sort });
  };

  const handleDelete = (view: View) => {
    const childCount = view.child_views?.length ?? 0;
    const msg =
      childCount > 0
        ? `Delete "${view.name}" and ${childCount} descendant${childCount > 1 ? 's' : ''}?`
        : `Delete "${view.name}"?`;
    if (window.confirm(msg)) del.mutate(view.id);
  };

  const renderNode = (id: string, depth = 0): React.ReactNode => {
    const view = viewsById.get(id);
    if (!view) return null;
    const isSelected = id === selected;
    const isHover = id === hoverId;
    const parent = parentByChild.get(id); // undefined for root
    const sortedSiblings = parent
      ? [...(viewsById.get(parent.parentId)?.child_views ?? [])].sort(
          (a, b) => a.sort - b.sort,
        )
      : [];
    const idx = sortedSiblings.findIndex((e) => e.related_view_id === id);
    const canMoveUp = parent != null && idx > 0;
    const canMoveDown = parent != null && idx >= 0 && idx < sortedSiblings.length - 1;
    const canDelete = id !== rootId; // protect the root for now
    const canDuplicate = parent != null;     // root has no parent edge to dup into
    const childCount = view.child_views?.length ?? 0;
    const hasChildren = childCount > 0;
    const isCollapsed = collapsed.has(id);

    return (
      <div key={id}>
        <div
          ref={isSelected ? selectedRowRef : null}
          onMouseEnter={() => setHoverId(id)}
          onMouseLeave={() => setHoverId((h) => (h === id ? null : h))}
          style={{
            ...styles.row,
            paddingLeft: 4 + depth * 12,
            ...(isSelected ? styles.rowSelected : {}),
          }}
        >
          {/*
            Fixed-width chevron slot keeps leaf nodes aligned with
            their siblings even when only the parent has children.
          */}
          <span style={styles.chevronSlot}>
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCollapsed(id);
                }}
                style={styles.chevronBtn}
                title={isCollapsed ? `Expand (${childCount} children)` : 'Collapse'}
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              >
                {isCollapsed ? '▸' : '▾'}
              </button>
            ) : null}
          </span>
          <button onClick={() => select(id)} style={styles.rowButton}>
            <span style={styles.rowType}>{view.Type}</span>
            <span style={styles.rowName}>{view.name}</span>
            {hasChildren && isCollapsed && (
              <span style={styles.childCount}>{childCount}</span>
            )}
          </button>
          {(isHover || isSelected) && (
            <span style={styles.actions}>
              <ActionBtn
                label="↑"
                title="Move up"
                disabled={!canMoveUp || swap.isPending}
                onClick={() => handleMove(id, 'up')}
              />
              <ActionBtn
                label="↓"
                title="Move down"
                disabled={!canMoveDown || swap.isPending}
                onClick={() => handleMove(id, 'down')}
              />
              <ActionBtn
                label="⎘"
                title={canDuplicate ? 'Duplicate (deep copy)' : 'Cannot duplicate root'}
                disabled={!canDuplicate || duplicate.isPending}
                onClick={() => duplicate.mutate(id)}
              />
              <ActionBtn
                label="✕"
                title={canDelete ? 'Delete' : 'Cannot delete root view'}
                disabled={!canDelete || del.isPending}
                onClick={() => handleDelete(view)}
                danger
              />
            </span>
          )}
        </div>
        {!isCollapsed &&
          [...(view.child_views ?? [])]
            .sort((a, b) => a.sort - b.sort)
            .map((edge: ChildViewEdge) => renderNode(edge.related_view_id, depth + 1))}
      </div>
    );
  };

  return (
    <section style={styles.section}>
      <Header onExpandAll={expandAll} onCollapseAll={collapseAll} />
      <div style={styles.tree}>{renderNode(rootId)}</div>
    </section>
  );
}

/* ---------------------------- Sub-components --------------------------- */

function Header({
  onExpandAll,
  onCollapseAll,
}: {
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) {
  return (
    <div style={styles.headerRow}>
      <h3 style={styles.heading}>View Tree</h3>
      <span style={styles.headerActions}>
        <button
          type="button"
          onClick={onExpandAll}
          style={styles.headerBtn}
          title="Expand every node"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={onCollapseAll}
          style={styles.headerBtn}
          title="Collapse every node with children"
        >
          Collapse all
        </button>
      </span>
    </div>
  );
}

function ActionBtn({
  label,
  title,
  disabled,
  onClick,
  danger,
}: {
  label: string;
  title: string;
  disabled?: boolean;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        ...styles.actionBtn,
        ...(danger ? styles.actionBtnDanger : {}),
        ...(disabled ? styles.actionBtnDisabled : {}),
      }}
    >
      {label}
    </button>
  );
}

/* ------------------------------- Helpers ------------------------------- */

function loadCollapsed(storageKey: string | null): Set<string> {
  if (!storageKey || typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

const styles: Record<string, React.CSSProperties> = {
  section: { padding: 12, flex: 1, overflow: 'auto' },
  headerRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 8,
  },
  heading: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    margin: 0,
    flex: 1,
  },
  headerActions: { display: 'flex', gap: 4 },
  headerBtn: {
    fontSize: 10,
    padding: '3px 8px',
    color: '#64748B',
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    cursor: 'pointer',
  },
  empty: { fontSize: 12, color: '#94A3B8' },
  tree: { fontSize: 12 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px 4px 0',
    borderRadius: 4,
  },
  rowSelected: { backgroundColor: '#EEF2FF', color: '#4F46E5' },
  chevronSlot: {
    width: 16,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevronBtn: {
    width: 16,
    height: 16,
    padding: 0,
    fontSize: 9,
    lineHeight: 1,
    color: '#64748B',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  rowButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'inherit',
    textAlign: 'left',
  },
  rowType: { fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 },
  rowName: {
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  // Small badge showing how many descendants are hidden when collapsed.
  childCount: {
    fontSize: 10,
    fontWeight: 600,
    color: '#94A3B8',
    backgroundColor: '#F1F5F9',
    padding: '0 5px',
    borderRadius: 8,
    flexShrink: 0,
  },
  actions: { display: 'flex', gap: 2, marginLeft: 4 },
  actionBtn: {
    width: 22, height: 22, padding: 0, fontSize: 12,
    color: '#64748B', backgroundColor: '#fff',
    border: '1px solid #E2E8F0', borderRadius: 4, cursor: 'pointer',
    lineHeight: '20px',
  },
  actionBtnDanger: { color: '#DC2626', borderColor: '#FECACA' },
  actionBtnDisabled: { opacity: 0.35, cursor: 'not-allowed' },
};
